import React from 'react';
import {
  ChatBubbleLeftIcon, 
  QueueListIcon, 
  Cog6ToothIcon,
  SparklesIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MusicalNoteIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import MyMusicPanel from './mymusic/MyMusicPanel';
import TrackReactionBar from './reactions/TrackReactionBar';
import useEventStore from '../stores/eventStore';
import useQueueStore from '../stores/queueStore';
import { useRoleStore } from '../stores/roleStore';
import usePlayerStore from '../stores/playerStore';
import useReactionStore from '../stores/reactionStore';
import {
  SIDE_PANEL_MIN_WIDTH,
  applySidePanelWidthVar,
  clampSidePanelWidth,
  readStoredSidePanelWidth,
  writeStoredSidePanelWidth,
} from '../utils/sidePanelWidth';
import { formatAddedByLabel } from '../utils/queueAttribution';
import { formatTrackTitleLine } from '../utils/trackDisplay';

const tabs = [
  { id: 'queue', icon: QueueListIcon, label: 'Queue' },
  { id: 'mymusic', icon: MusicalNoteIcon, label: 'My Music' },
  { id: 'chat', icon: ChatBubbleLeftIcon, label: 'Chat' },
  { id: 'ai', icon: SparklesIcon, label: 'AI' },
  { id: 'settings', icon: Cog6ToothIcon, label: 'Settings' },
];

export default function SidePanel({ open = false, onClose }) {
  const [activeTab, setActiveTab] = React.useState('queue');
  
  // Event and settings
  const eventData = useEventStore(state => state.eventData);
  const updateSettings = useEventStore(state => state.updateSettings);
  
  // Queue management
  const queue = useQueueStore(state => state.queue);
  const moveItem = useQueueStore(state => state.moveItem);
  const removeItem = useQueueStore(state => state.removeItem);
  
  // Player management
  const setCurrentSong = usePlayerStore(state => state.setCurrentSong);
  
  // Role check
  const userRole = useRoleStore(state => state.userRole);
  const userId = useRoleStore(state => state.userId);
  const can = useRoleStore(state => state.can);
  const isHost = userRole === 'host';
  const canManageQueue = can('canDeleteItems');
  const canEditSettings = can('canUpdateSettings');
  
  // Settings from event data
  const settings = eventData?.settings || {
    guestRequestsEnabled: true,
    voteSkipThreshold: 50,
    explicitFilter: true,
    requestInterval: 25
  };

  const [desktopWidth, setDesktopWidth] = React.useState(() => readStoredSidePanelWidth());
  const desktopWidthRef = React.useRef(desktopWidth);
  const draggingRef = React.useRef(false);

  React.useEffect(() => {
    useReactionStore.getState().bindScope({
      context: 'event',
      contextId: eventData?.id || null,
      userId,
    });
  }, [eventData?.id, userId]);

  React.useEffect(() => {
    desktopWidthRef.current = desktopWidth;
  }, [desktopWidth]);

  React.useLayoutEffect(() => {
    applySidePanelWidthVar(desktopWidth);
  }, [desktopWidth]);

  React.useEffect(() => {
    const onViewportResize = () => {
      setDesktopWidth((current) => {
        const next = clampSidePanelWidth(current, window.innerWidth);
        writeStoredSidePanelWidth(next);
        return next;
      });
    };
    window.addEventListener('resize', onViewportResize);
    return () => window.removeEventListener('resize', onViewportResize);
  }, []);

  React.useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, []);

  React.useEffect(() => {
    const isDesktop = typeof window.matchMedia === 'function'
      && window.matchMedia('(min-width: 768px)').matches;
    if (!open || isDesktop) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const stopDesktopResize = React.useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    writeStoredSidePanelWidth(desktopWidthRef.current);
  }, []);

  React.useEffect(() => {
    const onPointerMove = (event) => {
      if (!draggingRef.current) return;
      const next = clampSidePanelWidth(window.innerWidth - event.clientX);
      desktopWidthRef.current = next;
      setDesktopWidth(next);
      applySidePanelWidthVar(next);
    };
    const onPointerUp = () => {
      stopDesktopResize();
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [stopDesktopResize]);

  const handleResizePointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    draggingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const renderContent = () => {    switch (activeTab) {
      case 'queue':
        return (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {queue.map((song, index) => (
              <div
                key={song.queueItemId || `${song.id}-${index}`}
                className="flex items-center bg-white/5 p-2 rounded-lg group hover:bg-white/10 gap-2"
              >
                <span className="text-white/60 w-5 text-center shrink-0">{index + 1}</span>
                <div 
                  className={`flex items-center gap-2 flex-1 min-w-0 ${isHost ? 'cursor-pointer' : 'cursor-default'}`}
                  onClick={(e) => {
                    if (!isHost) return;
                    if (e.target.closest('button')) return;
                    setCurrentSong(song, { reason: 'song' });
                  }}
                >
                  <img
                    src={song.thumbnail}
                    alt=""
                    className="w-10 h-10 rounded object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{formatTrackTitleLine(song) || song.title}</p>
                    <p className="text-white/60 text-xs truncate">
                      {formatAddedByLabel(song.addedBy, eventData?.participants)}
                    </p>
                  </div>
                </div>
                <TrackReactionBar track={song} size="sm" />
                {canManageQueue && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveItem(index, index - 1);
                      }}
                      disabled={index === 0}
                      aria-label="Move song up"
                      className="hover:bg-white/10 rounded disabled:opacity-50 w-6 h-6 flex items-center justify-center"
                    >
                      <ArrowUpIcon className="w-3.5 h-3.5 text-white" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveItem(index, index + 1);
                      }}
                      disabled={index === queue.length - 1}
                      aria-label="Move song down"
                      className="hover:bg-white/10 rounded disabled:opacity-50 w-6 h-6 flex items-center justify-center"
                    >
                      <ArrowDownIcon className="w-3.5 h-3.5 text-white" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(index);
                      }}
                      aria-label="Remove song"
                      className="hover:bg-white/10 rounded text-red-400 w-6 h-6 flex items-center justify-center"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {queue.length === 0 && (
              <div className="text-center text-white/60 py-8">
                No songs in queue
              </div>
            )}
          </div>
        );

      case 'mymusic':
        return <MyMusicPanel />;

      case 'chat':
        return (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="text-center text-white/60 py-8">
              Chat feature coming soon
            </div>
          </div>
        );

      case 'ai':
        return (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="space-y-3">
              <article className="rounded-lg bg-white/5 p-4">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <h3 className="font-medium text-white">✨ Seamless Playback</h3>
                  <span className="shrink-0 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/70">
                    Coming Soon
                  </span>
                </div>
                <p className="text-sm text-white/60">
                  AI-powered AutoMix that keeps the mood going, selects the best portions
                  of each song, and adapts the music flow as the party evolves.
                </p>
              </article>

              <article className="rounded-lg bg-white/5 p-4">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <h3 className="font-medium text-white">💬 AI Chatbot</h3>
                  <span className="shrink-0 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/70">
                    Coming Soon
                  </span>
                </div>
                <p className="text-sm text-white/60">
                  Chat with Rhythmé's AI assistant about songs and control music playback
                  more naturally.
                </p>
              </article>
            </div>
          </div>
        );

      case 'settings':
        return canEditSettings ? (
          <div className="flex-1 overflow-y-auto p-4">
            <section aria-label="Guest permissions" className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-white/60">
                Guest permissions
              </h3>
              <label className="flex cursor-pointer items-start justify-between gap-4 rounded-lg bg-white/5 p-3">
                <span>
                  <span className="block font-medium text-white">
                    Guests can add songs directly
                  </span>
                  <span className="mt-1 block text-sm text-white/60">
                    {settings.guestRequestsEnabled
                      ? 'Songs are added directly to the queue.'
                      : 'Guest song additions are disabled.'}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={settings.guestRequestsEnabled}
                  onChange={(e) =>
                    updateSettings({ guestRequestsEnabled: e.target.checked })
                  }
                  className="sr-only peer"
                />
                <span className="relative mt-0.5 h-6 w-11 shrink-0 rounded-full bg-white/20 after:absolute after:left-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:content-[''] after:transition-all peer-checked:bg-emerald-600 peer-checked:after:translate-x-full peer-checked:after:border-white" />
              </label>
            </section>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-white/60">Only hosts can access settings</p>
          </div>
        );

      default:
        return null;
    }
  };

  const renderPanel = (showClose) => (
    <div className="flex min-h-0 flex-1 flex-col">
      {showClose && (
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <h2 className="truncate font-semibold text-white">
            {eventData?.title || 'Session'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 shrink-0 rounded-lg p-2 text-white hover:bg-white/10"
            aria-label="Close menu"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      )}

      <div className="flex overflow-x-auto border-b border-white/10">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex min-h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white/10 text-white'
                : 'text-white/60 hover:bg-white/5 hover:text-white'
            }`}
          >
            <tab.icon className="h-5 w-5 shrink-0" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );

  return (
    <>
      <aside
        className="hidden md:flex bg-black/50 backdrop-blur-xl border-l border-white/10 flex-col h-screen fixed right-0 top-0 z-20"
        style={{ width: desktopWidth }}
      >
        <div
          role="separator"
          aria-label="Resize side panel"
          aria-orientation="vertical"
          aria-valuemin={SIDE_PANEL_MIN_WIDTH}
          aria-valuemax={Math.round(typeof window !== 'undefined' ? window.innerWidth * 0.7 : 896)}
          aria-valuenow={desktopWidth}
          className="absolute inset-y-0 left-0 z-30 w-3 -translate-x-1/2 cursor-col-resize touch-none"
          onPointerDown={handleResizePointerDown}
        >
          <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/25" />
        </div>
        {renderPanel(false)}
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Close menu backdrop"
            onClick={onClose}
          />
          <aside
            className="absolute inset-y-0 right-0 left-10 flex flex-col border-l border-white/10 bg-black/90 backdrop-blur-xl"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {renderPanel(true)}
          </aside>
        </div>
      )}
    </>
  );
}